import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Modal, Pagination, Radio, Select, Upload } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Trophy,
  Plus,
  FileText,
  Trash2,
  FileSpreadsheet,
  ExternalLink,
  Edit3,
  Pin,
  PinOff,
  Newspaper,
  X as XIcon,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";
import useAxiosInstance from "../../lib/useAxiosInstance";
import formatIndianNumber from '../../utils/formatIndianNumber';


const formatPercent = (value) => {
  const number = Number(value) || 0;
  const percent = Math.abs(number) <= 1 ? number * 100 : number;
  return `${percent.toFixed(1)}%`;
};

// ─── Helpers ────────────────────────────────────────────────────────────────

// const formatIndianNumber = (num) => Number(num || 0).toLocaleString("en-IN");

// ─── Reusable Panel (accordion) ─────────────────────────────────────────────

export const Panel = ({ title, children, expanded, onToggle }) => (
  <div className="election-section overflow-hidden">
    <button
      type="button"
      className="election-panel-header w-full flex items-center justify-between"
      onClick={onToggle}
    >
      <span>{title}</span>
      <span
        className={`transition-transform duration-300 text-xs ${
          expanded ? "rotate-180" : ""
        }`}
      >
        ▼
      </span>
    </button>

    <div
      className={`transition-all duration-300 ease-in-out ${
        expanded ? "opacity-100 mt-3" : "max-h-0 opacity-0"
      }`}
    >
      {children}
    </div>
  </div>
);

// ─── Election Result Panel ───────────────────────────────────────────────────

export const ElectionResultPanel = ({ party, panels, constituency, expanded, onToggle, t }) => (
  <Panel
    title={`${t ? t("ElectionMode.electionResult") : "Election Result"} - ${party}`}
    expanded={expanded}
    onToggle={onToggle}
  >
    <div className="card-wrapper !rounded-xl p-3 mb-3 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <Trophy size={14} />
            {t ? t("ElectionMode.rank") : "Rank"}
          </div>
          <div className="text-3xl font-bold text-[#0b65c2]">
            #{panels.electionResult?.rank || "-"}
          </div>
        </div>
        {panels.electionResult?.category && (
          <div className="text-right">
            <div className="text-xs text-gray-600">{t ? t("ElectionMode.category") : "Category"}</div>
            <div className="text-sm font-bold text-[#0b65c2]">
              {panels.electionResult.category}
            </div>
          </div>
        )}
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="card-wrapper !rounded-xl px-3 py-2 shadow-sm">
        <div className="text-sm text-gray-600">{t ? t("ElectionMode.candidate") : "Candidate"}</div>
        <div className="text-sm font-semibold text-[#0b65c2]">
          {panels.electionResult?.candidateName || "-"}
        </div>
      </div>
      <div className="card-wrapper !rounded-xl px-3 py-2 shadow-sm">
        <div className="text-sm text-gray-600">{t ? t("ElectionMode.voteShare") : "Vote Share"}</div>
        <div className="text-lg font-semibold text-[#0b65c2]">
          {formatIndianNumber(panels.electionResult?.voteShare)}
        </div>
        <div className="text-xs text-black">
          {panels.electionResult?.voteSharePercent}%
        </div>
      </div>
    </div>

    {constituency && (
      <>
        <div
          className="card-wrapper !rounded-xl px-3 py-2 mt-2 shadow-sm"
          style={{
            border: `1px solid ${
              Number(panels.electionResult?.marginPercent) >= 0 ? "#16a34a" : "#dc2626"
            }55`,
          }}
        >
          <div className="text-sm text-gray-600">{t ? t("ElectionMode.margin") : "Margin"}</div>
          <div
            className="text-lg font-bold"
            style={{
              color:
                Number(panels.electionResult?.marginPercent) >= 0
                  ? "#16a34a"
                  : "#dc2626",
            }}
          >
            {Number(panels.electionResult?.marginPercent) >= 0
              ? (t ? t("ElectionMode.wonBy") : "Won by")
              : (t ? t("ElectionMode.lostBy") : "Lost by")}{" "}
            {/* {Math.abs(Number(panels.electionResult?.marginPercent) || 0)}% */}
            {formatPercent(panels.electionResult?.marginPercent) || 0}
          </div>
        </div>
        <LocationsPanel
          panels={panels}
          constituency={constituency}
          expanded
          onToggle={() => {}}
          t={t}
        />
      </>
    )}
  </Panel>
);

// ─── Seat Status Panel ───────────────────────────────────────────────────────

const SEAT_STYLES = {
  safe: {
    bg: "rgba(0, 80, 53, 0.2)",
    border: "1px solid rgba(0, 101, 67, 0.35)",
    text: "#005636ff",
  },
  battleground: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "1px solid rgba(245, 158, 11, 0.35)",
    text: "#f9b70fff",
  },
  favourable: {
    bg: "rgba(16, 185, 129, 0.12)",
    border: "1px solid rgba(16, 185, 129, 0.35)",
    text: "#22835fff",
  },
  difficult: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "1px solid rgba(239, 68, 68, 0.35)",
    text: "#f87171",
  },
  "very difficult": {
    bg: "rgba(237, 37, 37, 0.12)",
    border: "1px solid rgba(238, 32, 32, 0.35)",
    text: "#f92828ff",
  },
};

const SEAT_CATEGORY_KEYS = {
  safe: "safe",
  battleground: "battleground",
  favourable: "favourable",
  difficult: "difficult",
  "very difficult": "veryDifficult",
};

export const SeatStatusPanel = ({ panels, expanded, onToggle, t }) => (
  <Panel title={t ? t("ElectionMode.seatStatus") : "Seat Status"} expanded={expanded} onToggle={onToggle}>
    <div className="grid grid-cols-2 gap-3 mt-2 card-wrapper p-2 rounded-xl">
      {(panels.favourability || []).map((item) => {
        const style =
          SEAT_STYLES[item.category?.toLowerCase()] || SEAT_STYLES.difficult;
        return (
          <div
            key={item.category}
            className="rounded-xl px-2 py-3 transition-all duration-200"
            style={{
              background: style.bg,
              border: style.border,
              boxShadow: "0 0 12px rgba(0,0,0,0.15)",
            }}
          >
            <div
              className="text-xl font-bold leading-none"
              style={{ color: style.text }}
            >
              {item.count}
            </div>
            <div
              className="uppercase tracking-widest text-sm font-semibold mt-2"
              style={{ color: style.text }}
            >
              {t && SEAT_CATEGORY_KEYS[item.category?.toLowerCase()]
                ? t(`ElectionMode.${SEAT_CATEGORY_KEYS[item.category.toLowerCase()]}`)
                : item.category}
            </div>
          </div>
        );
      })}
    </div>
  </Panel>
);

// ─── Community Distribution Panel ───────────────────────────────────────────

export const CommunityPanel = ({ communityChart, expanded, onToggle, t }) => (
  <Panel
    title={t ? t("ElectionMode.communityDistribution") : "Community Distribution"}
    expanded={expanded}
    onToggle={onToggle}
  >
    <ResponsiveContainer width="100%" height={190}>
      <BarChart data={communityChart}>
        <XAxis dataKey="name" tick={{ fill: "#9aa7c7", fontSize: 10 }} />
        <YAxis tick={{ fill: "#9aa7c7", fontSize: 10 }} />
        <Tooltip />
        <Bar dataKey="value" fill="#4f8cff" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </Panel>
);

// ─── Religion Distribution Panel ────────────────────────────────────────────

const PIE_COLORS = ["#4f8cff", "#ff6b1a", "#37d67a"];

export const ReligionPanel = ({ religionChart, expanded, onToggle, t }) => (
  <Panel
    title={t ? t("ElectionMode.religionDistribution") : "Religion Distribution"}
    expanded={expanded}
    onToggle={onToggle}
  >
    <ResponsiveContainer width="100%" height={190}>
      <PieChart>
        <Pie
          data={religionChart}
          dataKey="value"
          nameKey="name"
          innerRadius={44}
          outerRadius={78}
        >
          {religionChart.map((entry, index) => (
            <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </Panel>
);

// ─── Top Locations Panel ─────────────────────────────────────────────────────

export const LocationsPanel = ({
  panels,
  constituency,
  expanded,
  onToggle,
  t,
}) => {
  const maxLocation = Math.max(
    ...(panels.topLocations || []).map((item) => item.count),
    1
  );

  return (
    <Panel
      title={t ? t("ElectionMode.topLocations") : "Top 5 Most Visited Location"}
      expanded={expanded}
      onToggle={onToggle}
    >
      {constituency ? (
        <div className="election-chip-row">
          {(panels.topLocations || []).map((item) => (
            <span className="election-chip active" key={item.name}>
              {item.name}
            </span>
          ))}
        </div>
      ) : (
        (panels.topLocations || []).map((item) => (
          <div key={item.name} className="mb-3">
            <div className="election-row !border-0 !py-1">
              <span>{item.name}</span>
              <strong>{item.count}</strong>
            </div>
            <div className="election-progress">
              <div
                className="election-progress-fill"
                style={{ width: `${(item.count / maxLocation) * 100}%` }}
              />
            </div>
          </div>
        ))
      )}

      {!panels.topLocations?.length && (
        <div className="election-empty">{t ? t("ElectionMode.noLocationsAvailable") : "No locations available"}</div>
      )}
    </Panel>
  );
};

// ─── Reports Tab ─────────────────────────────────────────────────────────────

const REPORT_STYLE = {
  bg: "rgba(79,140,255,0.12)",
  border: "rgba(79,140,255,0.35)",
  text: "#0b65c2",
};

const getReportFile = (report) => report.resourceLocation?.[0] || {};

// Reports tagged via the admin's scope picker carry `matchType`
// (district/block for State/Assembly district-level tags, constituency/village
// for the narrower ones). Older reports predate that field, so fall back to
// whichever of district/constituency is actually set.
const SCOPE_LABELS = { district: "District", block: "Block", constituency: "Constituency", village: "Village" };
const getReportScopeLabel = (report) => {
  if (report.matchType && SCOPE_LABELS[report.matchType]) {
    const value = ["district", "block"].includes(report.matchType) ? report.district : report.constituency;
    return `${SCOPE_LABELS[report.matchType]}: ${value || "-"}`;
  }
  if (report.district) return `District: ${report.district}`;
  if (report.constituency) return `Constituency: ${report.constituency}`;
  return "All constituencies";
};

const getFileKind = (fileName = "") => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".xls") || lower.endsWith(".xlsx") || lower.endsWith(".csv")) return "excel";
  if (lower.match(/\.(jpg|jpeg|png)$/)) return "image";
  return "document";
};

const ReportFileIcon = ({ kind }) => {
  if (kind === "excel") return <FileSpreadsheet size={14} style={{ color: "#15803d" }} />;
  return <FileText size={14} style={{ color: REPORT_STYLE.text }} />;
};

const ReportCard = ({ report, selected, onOpen }) => {
  const file = getReportFile(report);
  const kind = getFileKind(file.name);

  return (
    <button
      type="button"
      className="card-wrapper !rounded-xl px-3 py-3 mb-2 flex items-start gap-3 w-full text-left hover:shadow-md transition-all"
      style={{ border: `1px solid ${selected ? REPORT_STYLE.text : REPORT_STYLE.border}` }}
      onClick={() => onOpen(report)}
    >
      <div
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ background: REPORT_STYLE.bg }}
      >
        <ReportFileIcon kind={kind} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate text-black">{report.reportName}</div>
        <div className="text-xs text-gray-800 mt-0.5">
          {getReportScopeLabel(report)} · {new Date(report.createdAt).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </div>
        {report.reportDescription && (
          <div className="text-xs text-gray-600 mt-1 line-clamp-2">
            {report.reportDescription}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
          style={{ background: REPORT_STYLE.bg, color: REPORT_STYLE.text, border: `1px solid ${REPORT_STYLE.border}` }}
        >
          {kind}
        </span>
      </div>
    </button>
  );
};

const normalizeTags = (report) => {
  const tags = Array.isArray(report.tags) ? report.tags : [];
  const flatTags = tags
    .flatMap((tag) => String(tag || "").split(","))
    .map((tag) => tag.trim())
    .filter(Boolean);
  return flatTags.length ? flatTags : ["Untagged"];
};

const ReportPreview = ({ report }) => {
  if (!report) {
    return (
      <div className="election-empty text-center py-5">
        <FileText size={24} className="mx-auto mb-2 text-gray-300" />
        <div className="text-gray-400 text-sm">Select a report to preview</div>
      </div>
    );
  }

  const file = getReportFile(report);
  const kind = getFileKind(file.name);

  return (
    <div className="card-wrapper !rounded-xl p-3 mb-3">
      <div className="flex justify-between items-start gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{report.reportName}</div>
          {/* <div className="text-xs text-gray-500">{file.name}</div> */}
        </div>
        {file.publicUrl && (
          <button
            type="button"
            className="text-[#0b65c2] hover:text-[#0952a8]"
            onClick={() => window.open(file.publicUrl, "_blank", "noopener,noreferrer")}
            title="Open report"
          >
            <ExternalLink size={15} />
          </button>
        )}
      </div>

      {kind === "pdf" && file.publicUrl ? (
        <iframe title={report.reportName} src={file.publicUrl} className="w-full h-48 rounded-lg border border-gray-200" />
      ) : kind === "image" && file.publicUrl ? (
        <img src={file.publicUrl} alt={report.reportName} className="w-full max-h-48 object-contain rounded-lg border border-gray-200" />
      ) : (
        <div className="rounded-lg border border-gray-200 p-3 text-xs text-gray-500">
          {kind === "excel" ? "Excel preview is not available here. Open the report to view or download it." : "Preview is not available for this file type."}
        </div>
      )}

      {report.reportDescription && (
        <div className="text-xs text-gray-600 mt-2">{report.reportDescription}</div>
      )}
    </div>
  );
};

export const ReportsTab = ({
  electionModeId,
  filters,
  constituencyOptions,
  quickReportRequest,
  t,
}) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState(null);
  const [expandedTags, setExpandedTags] = useState({});

  const reportsQuery = useQuery(
    ["election-mode-reports", electionModeId, filters.district, filters.constituency],
    async () => {
      const response = await axiosInstance.get("api/reports/election-mode/list", {
        params: {
          electionModeId,
          district: filters.district || undefined,
          constituency: filters.constituency || undefined,
        },
      });
      return response.data?.data || [];
    },
    {
      enabled: Boolean(electionModeId),
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const reports = useMemo(() => reportsQuery.data || [], [reportsQuery.data]);
  const groupedReports = useMemo(() => {
    return reports.reduce((acc, report) => {
      normalizeTags(report).forEach((tag) => {
        acc[tag] = acc[tag] || [];
        acc[tag].push(report);
      });
      return acc;
    }, {});
  }, [reports]);

  console.log("groupedReports",groupedReports)

  const handleOpenReport = (report) => {
    setSelectedReport(report);
    // if (file.publicUrl) {
    //   window.open(file.publicUrl, "_blank", "noopener,noreferrer");
    // }
  };

  return (
    <div className="flex flex-col gap-2">
      <ReportPreview report={selectedReport} />

      <div className="mt-1">
        {reportsQuery.isLoading ? (
          <div className="election-empty text-center py-8">{t ? t("ElectionMode.loadingReports") : "Loading reports..."}</div>
        ) : reports.length === 0 ? (
          <div className="election-empty text-center py-8">
            <FileText size={28} className="mx-auto mb-2 text-gray-300" />
            <div className="text-gray-400 text-sm">{t ? t("ElectionMode.noReportsYet") : "No reports yet"}</div>
          </div>
        ) : (
          Object.entries(groupedReports).map(([tag, tagReports]) => {
            const expanded = expandedTags[tag] !== false;
            return (
              <Panel
                key={tag}
                title={`${tag} (${tagReports.length})`}
                expanded={expanded}
                onToggle={() =>
                  setExpandedTags((prev) => ({ ...prev, [tag]: !expanded }))
                }
              >
                {tagReports.map((report) => (
                  <ReportCard
                    key={report._id}
                    report={report}
                    selected={selectedReport?._id === report._id}
                    onOpen={handleOpenReport}
                  />
                ))}
              </Panel>
            );
          })
        )}
      </div>

    </div>
  );
};

const emptyNoteForm = {
  heading: "",
  scope: "state",
  district: "",
  constituency: "",
  party: "",
  content: "",
  pinned: false,
};

// Quill toolbar + image handler — images are converted to base64 and embedded inline
const QUILL_MODULES = {
  toolbar: {
    container: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ header: [1, 2, 3, false] }],
      ["link", "image"],
      ["clean"],
    ],
  },
};

const QUILL_FORMATS = [
  "bold", "italic", "underline", "strike",
  "list", "bullet",
  "header",
  "link", "image",
];

const NoteEditor = ({ value, onChange }) => {
  const quillRef = useRef(null);

  // Custom image handler: read file → insert base64 data URL into editor
  const imageHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", reader.result);
        quill.setSelection(range.index + 1);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // Wire up the custom image handler after mount
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const toolbar = quill.getModule("toolbar");
    toolbar.addHandler("image", imageHandler);
  }, []);

  return (
    <div className="note-quill-wrapper">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={QUILL_MODULES}
        formats={QUILL_FORMATS}
        style={{ minHeight: 220 }}
      />
    </div>
  );
};

// Lightbox for images embedded inside note content
const NoteImageLightbox = ({ src, onClose }) => (
  <div
    className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/70"
    onClick={onClose}
  >
    <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="absolute -top-4 -right-4 bg-white rounded-full p-1 shadow-lg text-slate-600 hover:text-red-500"
        onClick={onClose}
      >
        <XIcon size={16} />
      </button>
      <img src={src} alt="Note attachment" className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain" />
    </div>
  </div>
);

// Render stored HTML with small clickable thumbnails for embedded images
const NoteContentRenderer = ({ html }) => {
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Replace <img> tags so we can attach click handlers
  const processedHtml = useMemo(() => {
    if (!html) return "";
    // Add a small fixed-size style to all inline images for thumbnail display
    return html.replace(/<img /g, '<img style="max-width:120px;max-height:90px;object-fit:cover;border-radius:6px;cursor:pointer;border:1px solid #e2e8f0;margin:2px;" ');
  }, [html]);

  const handleClick = (e) => {
    if (e.target.tagName === "IMG") {
      setLightboxSrc(e.target.src);
    }
  };

  return (
    <>
      <div
        className="note-content-view rounded-xl border border-gray-200 bg-[#fffdf5] p-4 min-h-[120px] text-slate-800 prose max-w-none text-sm"
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        onClick={handleClick}
        style={{ cursor: "default" }}
      />
      {lightboxSrc && (
        <NoteImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
    </>
  );
};

const NoteModal = ({
  open,
  mode,
  form,
  setForm,
  districts,
  constituencies,
  parties,
  onCancel,
  onSave,
  t,
  districtLabel,
  constituencyLabel,
  hideDistrict,
}) => {
  const isView = mode === "view";
  const tr = (key, fallback) => (t ? t(`ElectionMode.${key}`) : fallback);
  const districtText = districtLabel || tr("district", "District");
  const constituencyText = constituencyLabel || tr("constituency", "Constituency");
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={860}
      zIndex={100000}
      wrapClassName="em-dark-modal"
      getContainer={() => document.body}
      footer={
        isView
          ? null
          : [
              <Button key="cancel" onClick={onCancel}>
                {tr("cancel", "Cancel")}
              </Button>,
              <Button key="save" type="primary" className="bg-[#0b65c2]" onClick={onSave}>
                {tr("saveNote", "Save Note")}
              </Button>,
            ]
      }
      title={isView ? form.heading : mode === "edit" ? tr("editNote", "Edit Note") : tr("addNote", "Add Note")}
    >
      {isView ? (
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="election-chip active">{form.scope}</span>
            {form.party && <span className="election-chip">{form.party}</span>}
            {form.district && <span className="election-chip">{form.district}</span>}
            {form.constituency && <span className="election-chip">{form.constituency}</span>}
            {/* {form.createdBy && (
              <span className="election-chip text-slate-500">
                ({typeof form.createdBy === "object" ? form.createdBy?.name || form.createdBy?.email || "Unknown" : form.createdBy})
              </span>
            )} */}
          </div>
          <NoteContentRenderer html={form.content} />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">{tr("heading", "Heading")}</div>
            <Input
              value={form.heading}
              placeholder={tr("noteHeadingPlaceholder", "Note heading")}
              onChange={(event) => setForm((prev) => ({ ...prev, heading: event.target.value }))}
            />
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-500 mb-1">{tr("createNoteFor", "Create note for")}</div>
            <Radio.Group
              value={form.scope}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  scope: event.target.value,
                  district: event.target.value === "district" ? prev.district : "",
                  constituency: event.target.value === "constituency" ? prev.constituency : "",
                }))
              }
            >
              <Radio.Button value="state">{tr("state", "State")}</Radio.Button>
              {!hideDistrict && (
                <Radio.Button value="district">{districtText}</Radio.Button>
              )}
              <Radio.Button value="constituency">{constituencyText}</Radio.Button>
            </Radio.Group>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">{tr("party", "Party")}</div>
              <Select
                className="w-full"
                placeholder={tr("selectParty", "Select party")}
                value={form.party || undefined}
                onChange={(value) => setForm((prev) => ({ ...prev, party: value }))}
                options={parties.map((party) => ({ value: party, label: party }))}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </div>
            {form.scope === "district" && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">{districtText}</div>
                <Select
                  className="w-full"
                  placeholder={districtText}
                  value={form.district || undefined}
                  onChange={(value) => setForm((prev) => ({ ...prev, district: value }))}
                  options={districts.map((district) => ({ value: district, label: district }))}
                  showSearch
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </div>
            )}
            {form.scope === "constituency" && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">{constituencyText}</div>
                <Select
                  className="w-full"
                  placeholder={constituencyText}
                  value={form.constituency || undefined}
                  onChange={(value) => setForm((prev) => ({ ...prev, constituency: value }))}
                  options={constituencies.map((constituency) => ({
                    value: constituency,
                    label: constituency,
                  }))}
                  showSearch
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </div>
            )}
          </div>

          <NoteEditor
            value={form.content}
            onChange={(content) => setForm((prev) => ({ ...prev, content }))}
          />
        </div>
      )}
    </Modal>
  );
};

const MLA_FIELDS = [
  ["party", "Party"],
  ["constituency", "Constituency"],
  ["district", "District"],
  ["previousParty", "Previous Party"],
  ["fatherName", "Father's Name"],
  ["dateOfBirth", "Date of Birth"],
  ["placeOfBirth", "Place of Birth"],
  ["religion", "Religion"],
  ["caste", "Caste"],
  ["education", "Education"],
  ["business", "Business"],
  ["totalProperties", "Total Properties"],
  ["temporaryAddress", "Temporary Address"],
  ["mobileNumber", "Mobile Number"],
  ["email", "Email"],
  ["bjpStatus", "BJP Status"],
];

export const MlaAffidavitPanel = ({ electionModeId, filters, t }) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();

  const affidavitsQuery = useQuery(
    ["election-mode-mla-affidavits", electionModeId, filters.district, filters.constituency],
    async () => {
      const response = await axiosInstance.get(
        `api/electionMode/${electionModeId}/mla-affidavit`,
        {
          params: {
            district: filters.district || undefined,
            constituency: filters.constituency || undefined,
          },
        }
      );
      return response.data?.data || [];
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1 }
  );

  const rows = affidavitsQuery.data || [];

  return (
    <div className="border-t border-slate-200 !flex-col !justify-center items-center">
        <div className="font-semibold text-2xl text-black text-center">{t ? t("ElectionMode.mlaAffidavit") : "MLA Affidavit"}</div>

      <div className="mt-3 flex flex-col gap-2">
        {affidavitsQuery.isLoading ? (
          <div className="election-empty text-center py-4">{t ? t("ElectionMode.loadingMlaDetails") : "Loading MLA details..."}</div>
        ) : rows.length === 0 ? (
          <div className="election-empty text-center py-4">
            Select a district or constituency to view MLA details.
          </div>
        ) : (
          rows.map((row) => (
            <div key={row._id} className="card-wrapper !rounded-xl p-3">
              <div className="font-semibold text-sm text-black">{row.name || "-"}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-700">
                {MLA_FIELDS.map(([key, label]) => (
                  row[key] ? (
                    <div key={key} className="rounded-lg bg-white/70 p-2">
                      <div className="text-slate-500">{label}</div>
                      <strong>{row[key]}</strong>
                    </div>
                  ) : null
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export const NotesPanel = ({
  electionModeId,
  filters,
  districts,
  constituencies,
  parties,
  quickNoteRequest,
  t,
  districtLabel,
  constituencyLabel,
  hideDistrict,
}) => {
  const axiosInstance = useAxiosInstance();
  const queryClient = useQueryClient();
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [form, setForm] = useState(emptyNoteForm);

  const notesQuery = useQuery(
    ["election-mode-notes", electionModeId, filters.district, filters.constituency, filters.party],
    async () => {
      const response = await axiosInstance.get("api/electionMode/notes/list", {
        params: {
          electionModeId,
          district: filters.district || undefined,
          constituency: filters.constituency || undefined,
          party: filters.party || undefined,
        },
      });
      return response.data?.data || [];
    },
    { enabled: Boolean(electionModeId), refetchOnWindowFocus: false, retry: 1 }
  );

  const notes = notesQuery.data || [];

  const saveMutation = useMutation(
    (payload) => {
      if (selectedNoteId) {
        return axiosInstance.put(`api/electionMode/notes/${selectedNoteId}`, payload);
      }
      return axiosInstance.post("api/electionMode/notes", payload);
    },
    {
      onSuccess: () => {
        toast.success(selectedNoteId ? "Note updated" : "Note created");
        setModalOpen(false);
        setSelectedNoteId(null);
        queryClient.invalidateQueries("election-mode-notes");
      },
      onError: () => toast.error("Unable to save note"),
    }
  );

  const deleteMutation = useMutation(
    (id) => axiosInstance.delete(`api/electionMode/notes/${id}`),
    {
      onSuccess: () => {
        toast.success("Note deleted");
        queryClient.invalidateQueries("election-mode-notes");
      },
      onError: () => toast.error("Unable to delete note"),
    }
  );

  const openAdd = (preset = {}) => {
    setSelectedNoteId(null);
    setModalMode("add");
    setForm({
      ...emptyNoteForm,
      party: filters.party || "",
      ...preset,
    });
    setModalOpen(true);
  };


  const openEdit = (note) => {
    setSelectedNoteId(note._id);
    setModalMode("edit");
    setForm({ ...emptyNoteForm, ...note });
    setModalOpen(true);
  };

  const openView = (note) => {
    setSelectedNoteId(note._id);
    setModalMode("view");
    setForm({ ...emptyNoteForm, ...note });
    setModalOpen(true);
  };

  const togglePin = (note) => {
    setSelectedNoteId(note._id);
    saveMutation.mutate({ ...note, pinned: !note.pinned });
  };

  const handleSave = () => {
    if (!form.heading.trim()) {
      toast.error(t ? t("ElectionMode.headingRequired") : "Heading is required");
      return;
    }
    if (form.scope === "district" && !form.district) {
      toast.error("District is required");
      return;
    }
    if (form.scope === "constituency" && !form.constituency) {
      toast.error("Constituency is required");
      return;
    }
    saveMutation.mutate({
      ...form,
      electionModeId,
    });
  };

  return (
    <div className="election-dashboard-right card-wrapper">
      <button
        type="button"
        className="w-full flex items-center justify-center gap-2 bg-[#0b65c2] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#0952a8] transition-colors shadow-sm !text-white"
        onClick={() => openAdd()}
      >
        <Plus size={15} />
        {t ? t("ElectionMode.addNote") : "Add Note"}
      </button>

      <div className="mt-3 flex flex-col gap-2">
        {notesQuery.isLoading ? (
          <div className="election-empty text-center py-8">{t ? t("ElectionMode.loadingNotes") : "Loading notes..."}</div>
        ) : notes.length === 0 ? (
          <div className="election-empty text-center py-8">{t ? t("ElectionMode.noNotesYet") : "No notes yet"}</div>
        ) : (
          notes.map((note) => (
            <button
              type="button"
              key={note._id}
              onClick={() => openView(note)}
              className="card-wrapper !rounded-xl p-3 text-left border border-white/40 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-black truncate">
                    {note.pinned ? "Pinned · " : ""}
                    {note.heading}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {note.scope}
                    {note.district ? ` · ${note.district}` : ""}
                    {note.constituency ? ` · ${note.constituency}` : ""}
                    {note.party ? ` · ${note.party}` : ""}
                    {/* {note.createdBy
                      ? ` (${typeof note.createdBy === "object" ? note.createdBy?.name || note.createdBy?.email || "Unknown" : note.createdBy})`
                      : ""} */}
                  </div>
                </div>
                {/* <div className="flex gap-1">
                  <Button
                    size="small"
                    icon={note.pinned ? <PinOff size={13} /> : <Pin size={13} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      togglePin(note);
                    }}
                  />
                  <Button
                    size="small"
                    icon={<Edit3 size={13} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEdit(note);
                    }}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<Trash2 size={13} />}
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteMutation.mutate(note._id);
                    }}
                  />
                </div> */}
                <div className="flex gap-1">
  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      togglePin(note);
    }}
    className="px-2 py-1 border rounded !text-black"
  >
    {note.pinned ? <PinOff size={13} /> : <Pin size={13} />}
  </button>

  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      openEdit(note);
    }}
    className="px-2 py-1 border rounded !text-black"
  >
    <Edit3 size={13} />
  </button>

  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      deleteMutation.mutate(note._id);
    }}
    className="px-2 py-1 border rounded text-red-500"
  >
    <Trash2 size={13} />
  </button>
</div>
              </div>
            </button>
          ))
        )}
      </div>

      <NoteModal
        open={modalOpen}
        mode={modalMode}
        form={form}
        setForm={setForm}
        districts={districts}
        constituencies={constituencies}
        parties={parties}
        onCancel={() => setModalOpen(false)}
        onSave={handleSave}
        t={t}
        districtLabel={districtLabel}
        constituencyLabel={constituencyLabel}
        hideDistrict={hideDistrict}
      />
    </div>
  );
};

// ─── Tab Bar ─────────────────────────────────────────────────────────────────

const TABS = ["Electoral", "Reports"];
const TAB_KEYS = { Electoral: "electoral", Reports: "reports" };

export const TabBar = ({ activeTab, onTabChange, t }) => (
  <div className="flex gap-1 mb-3 p-1 bg-gray-100 rounded-xl">
    {TABS.map((tab) => (
      <button
        key={tab}
        type="button"
        onClick={() => onTabChange(tab)}
        className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
          activeTab === tab
            ? "bg-white text-[#0b65c2] shadow-sm"
            : "text-gray-500 hover:text-gray-700"
        }`}
      >
        {t ? t(`ElectionMode.${TAB_KEYS[tab]}`) : tab}
      </button>
    ))}
  </div>
);

// ─── Electoral Tab ────────────────────────────────────────────────────────────

export const ElectoralTab = ({
  filters,
  panels,
  communityChart,
  religionChart,
  expandedPanels,
  onToggle,
  t
}) => (
  <>
    <ElectionResultPanel
      party={filters.party}
      panels={panels}
      constituency={filters.constituency}
      expanded={expandedPanels.electionResult}
      onToggle={() => onToggle("electionResult")}
      t={t}
    />
    {!filters.constituency && (
      <SeatStatusPanel
        panels={panels}
        expanded={expandedPanels.favourability}
        onToggle={() => onToggle("favourability")}
        t={t}
      />
    )}
    <CommunityPanel
      communityChart={communityChart}
      expanded={expandedPanels.community}
      onToggle={() => onToggle("community")}
      t={t}
    />
    {/* <ReligionPanel
      religionChart={religionChart}
      expanded={expandedPanels.religion}
      onToggle={() => onToggle("religion")}
    /> */}
    {!filters.constituency && (
      <LocationsPanel
        panels={panels}
        constituency={filters.constituency}
        expanded={expandedPanels.locations}
        onToggle={() => onToggle("locations")}
        t={t}
      />
    )}
  </>
);

// ─── Right Panel (main export) ────────────────────────────────────────────────

const RightPanel = ({
  electionModeId,
  filters,
  constituencyOptions,
  panels,
  communityChart,
  religionChart,
  expandedPanels,
  onToggle,
  activeTab,
  onActiveTabChange,
  quickReportRequest,
  t
}) => {
  return (
    <div className="election-dashboard-right card-wrapper">
      <TabBar activeTab={activeTab} onTabChange={onActiveTabChange} t={t} />

      {activeTab === "Electoral" ? (
        <ElectoralTab
          filters={filters}
          panels={panels}
          communityChart={communityChart}
          religionChart={religionChart}
          expandedPanels={expandedPanels}
          onToggle={onToggle}
          t={t}
        />
      ) : (
        <ReportsTab
          electionModeId={electionModeId}
          filters={filters}
          constituencyOptions={constituencyOptions}
          quickReportRequest={quickReportRequest}
          t={t}
        />
      )}
    </div>
  );
};

// ─── News Panel (client) ──────────────────────────────────────────────────────

const TODAY = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

const categoryColors = {
  default: { bg: "bg-slate-100", text: "text-slate-600" },
  0: { bg: "bg-orange-50", text: "text-orange-600" },
  1: { bg: "bg-blue-50", text: "text-blue-600" },
  2: { bg: "bg-green-50", text: "text-green-700" },
  3: { bg: "bg-purple-50", text: "text-purple-600" },
  4: { bg: "bg-pink-50", text: "text-pink-600" },
  5: { bg: "bg-yellow-50", text: "text-yellow-700" },
};

const getCategoryStyle = (cat, catList) => {
  const idx = catList.indexOf(cat);
  return categoryColors[idx % 6] || categoryColors.default;
};

const parseDate = (d) => {
  if (!d) return null;
  // Accept "YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", Excel serial numbers
  if (typeof d === "number") {
    // Excel date serial
    const date = new Date(Math.round((d - 25569) * 86400 * 1000));
    return date.toISOString().slice(0, 10);
  }
  const parts = String(d).split(/[-/]/);
  if (parts.length === 3) {
    if (parts[0].length === 4) return String(d).slice(0, 10); // YYYY-MM-DD
    // DD-MM-YYYY → YYYY-MM-DD
    return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
  }
  return String(d).slice(0, 10);
};

const BREAKING_NEWS_CATEGORY = "ब्रेकिंग न्यूज़";

// Format a Mongo ISO timestamp as a readable date + time.
const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Strip HTML tags so the (rich-text) news message can be previewed as plain text.
const stripHtml = (html) => {
  if (!html) return "";
  if (typeof document !== "undefined") {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  }
  return String(html).replace(/<[^>]*>/g, "");
};

// True when the given timestamp falls on the current calendar day.
const isTodayDate = (value) => {
  if (!value) return false;
  const d = new Date(value);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

// Single news card — shows a 2–3 line preview; clicking opens the detail modal.
export const NewsReportCard = ({ row, catList, onOpen }) => {
  const { bg, text } = getCategoryStyle(row.category, catList);
  const isBreaking =
    row.isBreaking === 1 || row.category === BREAKING_NEWS_CATEGORY;
  const isToday = isTodayDate(row.createdAt);

  return (
    <div
      onClick={() => onOpen(row)}
      className={`rounded-xl p-3 border cursor-pointer transition-all duration-200 hover:shadow-md ${
        isBreaking
          ? "border-red-300 bg-red-50/60 shadow-sm"
          : "border-slate-200 bg-white/80"
      }`}
    >
      {isBreaking && (
        <div className="flex items-center gap-1 mb-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">
            {BREAKING_NEWS_CATEGORY}
          </span>
        </div>
      )}
      <p
        className="text-[13px] text-slate-800 leading-snug font-medium mb-2"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {stripHtml(row.message) || "-"}
      </p>
      <div className="flex flex-wrap gap-1 items-center text-[11px]">
        {row.district && (
          <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
            {row.district}
          </span>
        )}
        {row.constituency && (
          <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
            {row.constituency}
          </span>
        )}
        {row.category && !isBreaking && (
          <span className={`${bg} ${text} px-2 py-0.5 rounded-full`}>
            {row.category}
          </span>
        )}
        {isToday && (
          <span className="bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">
            Today
          </span>
        )}
        {row.createdAt && (
          <span className="ml-auto text-slate-400">
            {formatDateTime(row.createdAt)}
          </span>
        )}
      </div>
    </div>
  );
};

// Full-detail modal for a single news item.
export const NewsDetailModal = ({ row, open, onClose, t }) => {
  const isBreaking =
    row?.isBreaking === 1 || row?.category === BREAKING_NEWS_CATEGORY;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={620}
      centered
      zIndex={10050}
      maskClosable
      wrapClassName="em-dark-modal"
      getContainer={() => document.body}
    >
      {row && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {isBreaking && (
              <span className="text-[11px] font-bold text-red-600 uppercase tracking-wide bg-red-50 px-2 py-0.5 rounded-full">
                {BREAKING_NEWS_CATEGORY}
              </span>
            )}
            {row.category && !isBreaking && (
              <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                {row.category}
              </span>
            )}
            <span className="ml-auto text-[12px] text-slate-400">
              {formatDateTime(row.createdAt)}
            </span>
          </div>

          <div
            className="news-message-html text-[15px] text-slate-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: row.message || "-" }}
          />

          <div className="flex flex-wrap gap-2 text-[12px] pt-2 border-t border-slate-100">
            {row.district && (
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {t("ElectionMode.district")}: {row.district}
              </span>
            )}
            {row.constituency && (
              <span className="bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full">
                {t("ElectionMode.constituency")}: {row.constituency}
              </span>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

// Quick date preset helpers
const getDateRange = (preset) => {
  const now = new Date();
  const toStr = (d) => d.toISOString().slice(0, 10);
  if (preset === "today") {
    return { from: TODAY, to: TODAY };
  }
  if (preset === "last7") {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: toStr(d), to: TODAY };
  }
  if (preset === "thisMonth") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toStr(d), to: TODAY };
  }
  return { from: "", to: "" };
};

export const NewsPanelClient = ({ electionModeId, filters, t }) => {
  const tr = (key, fallback) => (t ? t(`ElectionMode.${key}`) : fallback);
  const axiosInstance = useAxiosInstance();
  const [filterCategory, setFilterCategory] = useState("");
  const [selectedNews, setSelectedNews] = useState(null);
  // "" | "today" | "last7" | "lastMonth" | "custom"
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const loadMoreRef = useRef(null);

  const PAGE_SIZE = 10;

  const region = filters?.region || "";
  const district = filters?.district || "";
  const constituency = filters?.constituency || "";

  // Effective date params sent to the backend.
  const isCustom = dateRange === "custom";
  const effectiveStartDate = isCustom ? startDate : "";
  const effectiveEndDate = isCustom ? endDate : "";

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    [
      "election-mode-news-reports",
      electionModeId,
      region,
      district,
      constituency,
      filterCategory,
      dateRange,
      effectiveStartDate,
      effectiveEndDate,
    ],
    async ({ pageParam = 1 }) => {
      const res = await axiosInstance.get(
        `api/electionMode/${electionModeId}/news-reports`,
        {
          params: {
            page: pageParam,
            limit: PAGE_SIZE,
            region: region || undefined,
            district: district || undefined,
            constituency: constituency || undefined,
            category: filterCategory || undefined,
            dateRange: dateRange || undefined,
            startDate: effectiveStartDate || undefined,
            endDate: effectiveEndDate || undefined,
          },
        }
      );
      return res.data?.data || {};
    },
    {
      enabled: Boolean(electionModeId),
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true,
      getNextPageParam: (lastPage) =>
        lastPage?.hasMore ? (lastPage.page || 1) + 1 : undefined,
    }
  );

  const pages = data?.pages || [];
  const allRows = useMemo(
    () => pages.flatMap((p) => p?.news || []),
    [pages]
  );
  const total = pages[0]?.total ?? allRows.length;
  const categories = pages[0]?.categories || [];

  // Infinite scroll — load the next page when the sentinel enters the viewport.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, allRows.length]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
        {tr("loadingNews", "Loading news…")}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
        <div className="text-4xl">⚠️</div>
        <div className="text-sm font-medium">{tr("failedToLoadNews", "Failed to load news")}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* ── Category filter ─────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <Select
          allowClear
          showSearch
          placeholder={tr("category", "Category")}
          size="small"
          value={filterCategory || undefined}
          onChange={(v) => setFilterCategory(v || "")}
          options={categories.map((c) => ({ label: c, value: c }))}
          className="flex-1"
          getPopupContainer={() => document.body}
          dropdownStyle={{ zIndex: 10050 }}
        />
      </div>

      {/* ── Date filters ────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {[
          { key: "today", label: tr("today", "Today"), active: "bg-orange-400 text-white border-orange-400" },
          { key: "last7", label: tr("last7Days", "Last 7 Days"), active: "bg-blue-500 text-white border-blue-500" },
          { key: "lastMonth", label: tr("lastMonth", "Last Month"), active: "bg-green-500 text-white border-green-500" },
          { key: "custom", label: tr("dateRange", "Date Range"), active: "bg-purple-500 text-white border-purple-500" },
        ].map(({ key, label, active }) => (
          <button
            key={key}
            onClick={() => {
              setDateRange(dateRange === key ? "" : key);
              if (key !== "custom") {
                setStartDate("");
                setEndDate("");
              }
            }}
            className={`text-[11px] px-3 py-1 rounded-full border transition-all ${
              dateRange === key
                ? active
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
            }`}
          >
            {label}
          </button>
        ))}
        {(dateRange || filterCategory) && (
          <button
            onClick={() => {
              setDateRange("");
              setStartDate("");
              setEndDate("");
              setFilterCategory("");
            }}
            className="text-[11px] text-slate-400 hover:text-red-500 transition-colors ml-auto"
          >
            {tr("clear", "Clear")}
          </button>
        )}
      </div>

      {/* Custom date range inputs */}
      {isCustom && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="flex-1 text-[11px] border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-400"
          />
          <span className="text-slate-400 text-[11px]">–</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="flex-1 text-[11px] border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-400"
          />
        </div>
      )}

      <div className="text-[11px] text-slate-400">
        {total} {total === 1 ? tr("record", "record") : tr("records", "records")}
        {(region || district || constituency || dateRange || filterCategory) &&
          ` (${tr("filtered", "filtered")})`}
      </div>

      {/* ── News list (infinite scroll) ─────────────────────── */}
      <div className="flex flex-col gap-2 overflow-y-auto flex-1 pb-2">
        {allRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
            <div className="text-4xl">📰</div>
            <div className="text-sm font-medium">{tr("noNewsAvailable", "No news available")}</div>
          </div>
        ) : (
          <>
            {allRows.map((row) => (
              <NewsReportCard
                key={row._id}
                row={row}
                catList={categories}
                onOpen={setSelectedNews}
              />
            ))}

            {/* Sentinel for infinite scroll */}
            <div ref={loadMoreRef} className="h-6" />

            {isFetchingNextPage && (
              <div className="text-center text-[11px] text-slate-400 py-2">
                {tr("loadingMore", "Loading more…")}
              </div>
            )}
            {!hasNextPage && allRows.length > 0 && (
              <div className="text-center text-[11px] text-slate-300 py-2">
                {tr("endOfNews", "— End of news —")}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Detail modal ────────────────────────────────────── */}
      <NewsDetailModal
        row={selectedNews}
        open={Boolean(selectedNews)}
        onClose={() => setSelectedNews(null)}
        t = {t}
      />
    </div>
  );
};

export default RightPanel;
